"use client"

import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/ui/card"
import { cn } from "@repo/ui/lib/utils"
import { motion, useInView } from "framer-motion"
import { ArrowRight, Check } from "lucide-react"
import { useRef } from "react"
import { HOME_LOGO_CLASS_CARD } from "./home-marketing-content"
import {
  DENDROCARE_SITE_URL,
  getPricingHref,
  navigateToPricingTab,
  type PricingTabValue,
} from "./pricing-anchors"

interface SolutionsSectionProps {
  content: Record<string, string>
}

interface SolutionItem {
  iconSrc: string
  title: string
  description: string
  features: string[]
  ctaLabel: string
  ctaHref: string
  ctaExternal: boolean
  featured?: boolean
  pricingTab?: PricingTabValue
}

export function SolutionsSection({ content }: SolutionsSectionProps): JSX.Element {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const solutions: SolutionItem[] = [
    {
      iconSrc: "/images/logos/dendromap.svg",
      title: "DENDROMAP",
      description: "Un outil pour cartographier, inventorier et gérer vos arbres.",
      features: ["Géolocalisation GPS", "Inventaire complet", "État sanitaire"],
      ctaLabel: "Découvrir les forfaits DENDROMAP",
      ctaHref: getPricingHref("basic"),
      ctaExternal: false,
      pricingTab: "basic",
      featured: false,
    },
    {
      iconSrc: "/images/logos/dendro-advanced.svg",
      title: "DENDROMAP+",
      description:
        "Un outil plus complet pour cartographier vos arbres, espaces boisés et alignements.",
      features: [
        "Inventaire + diagnostic",
        "Gestion centralisée & planification",
        "Gain de temps terrain & administratif",
      ],
      ctaLabel: "Découvrir les forfaits DENDROMAP+",
      ctaHref: getPricingHref("advanced"),
      ctaExternal: false,
      pricingTab: "advanced",
      featured: true,
    },
    {
      iconSrc: "/images/logos/dendrocare.svg",
      title: "DENDROCARE",
      description: "Des produits naturels pour soigner vos arbres et favoriser leur régénération.",
      features: [
        "Renforce naturellement la vitalité",
        "100 % naturel et respectueux",
        "Purifie et protège grâce à l'argile",
      ],
      ctaLabel: "Découvrir sur Dendrocare.fr",
      ctaHref: content["solutions.item3.ctaUrl"] || DENDROCARE_SITE_URL,
      ctaExternal: true,
      featured: false,
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  }

  const cardVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
    },
  }

  return (
    <section id="solutions" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto flex flex-col gap-16 px-4 sm:px-6 lg:px-8">

        {/* ─── Header ───────────────────────────────────────────────── */}
        <motion.div
          className="flex flex-col gap-4 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <Badge variant="outline" className="mx-auto w-fit">
            Nos solutions
          </Badge>
          <h2 className="text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
            {content["solutions.title"] || "Des solutions"}{" "}
            <span className="text-primary">
              {content["solutions.subtitle"] || "adaptées à vos besoins"}
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground leading-relaxed">
            {content["solutions.description"] ||
              "Que vous soyez une collectivité, une entreprise ou un expert, nous avons la solution qu'il vous faut."}
          </p>
        </motion.div>

        {/* ─── Cards ────────────────────────────────────────────────── */}
        <motion.div
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:items-stretch"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {solutions.map((solution) => (
            <motion.div
              key={solution.iconSrc}
              variants={cardVariants}
              className="h-full"
              whileHover={{ y: solution.featured ? -6 : -4 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Card
                className={cn(
                  "relative flex h-full flex-col transition-all duration-300",
                  solution.featured
                    ? "card-featured border-2 border-primary/40 shadow-forest-lg scale-[1.02]"
                    : "border hover:border-primary/20 hover:shadow-forest",
                )}
              >
                {/* Featured badge */}
                {solution.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <Badge
                      className="px-3 py-1 text-xs font-semibold shadow-sm"
                      style={{
                        background: "linear-gradient(135deg, #16a34a, #22c55e)",
                        border: "none",
                      }}
                    >
                      ✦ Recommandé
                    </Badge>
                  </div>
                )}

                <CardHeader className={cn("pb-4", solution.featured && "pt-8")}>
                  {/* Logo */}
                  <div
                    className={cn(
                      "mb-5 flex min-h-16 items-center rounded-xl px-4 py-2 sm:min-h-[4.5rem]",
                      solution.featured
                        ? "bg-primary/15 w-fit"
                        : "bg-muted w-fit",
                    )}
                  >
                    <img
                      src={solution.iconSrc}
                      alt=""
                      className={HOME_LOGO_CLASS_CARD}
                      aria-hidden
                    />
                  </div>
                  <CardTitle className="text-lg font-semibold">{solution.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {solution.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1 pb-4">
                  <ul className="flex flex-col gap-2.5">
                    {solution.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Check
                          className={cn(
                            "mt-0.5 size-4 shrink-0",
                            solution.featured ? "text-primary" : "text-primary/70",
                          )}
                          aria-hidden
                        />
                        <span className="text-muted-foreground">{feature.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-0">
                  {solution.ctaExternal ? (
                    <Button
                      variant="outline"
                      className="w-full hover-shimmer"
                      asChild
                    >
                      <a
                        href={solution.ctaHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {solution.ctaLabel}
                        <ArrowRight className="size-4" aria-hidden data-icon="inline-end" />
                      </a>
                    </Button>
                  ) : solution.featured ? (
                    <Button
                      className="w-full hover-shimmer shadow-forest"
                      onClick={() => navigateToPricingTab(solution.pricingTab!)}
                    >
                      {solution.ctaLabel}
                      <ArrowRight className="size-4" aria-hidden data-icon="inline-end" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full hover-shimmer"
                      onClick={() => navigateToPricingTab(solution.pricingTab!)}
                    >
                      {solution.ctaLabel}
                      <ArrowRight className="size-4" aria-hidden data-icon="inline-end" />
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
